import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Info, FileText, Shield } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { usePageSeo } from "@/hooks/usePageSeo";

const AboutUs = () => {
  usePageSeo({
    title: 'About PropertyTaxHero — Who We Are & Our Mission | PropertyTaxHero',
    description: 'PropertyTaxHero is a free public service that helps homeowners check their property tax assessments, find comparable sales, and prepare appeals. Learn about our mission and commitment to transparency.',
    path: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About PropertyTaxHero',
      url: 'https://www.propertytaxhero.com/about',
      mainEntity: {
        '@type': 'Organization',
        name: 'PropertyTaxHero',
        url: 'https://www.propertytaxhero.com',
        description: 'Free property tax appeal tool for homeowners across all 50 states.'
      }
    }
  });

  const isLoggedIn = false;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container mx-auto px-4 py-8 flex-1">
        <article className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-tax-deepBlue mb-8">About PropertyTaxHero — The Official Free Property Tax Appeal Tool</h1>

          <p className="text-lg text-tax-neutralGray mb-8 leading-relaxed">
            PropertyTaxHero is the official free tool that helps homeowners across all 50 states check whether their property has been overassessed by their county. We provide comparable sales data, appeal deadline information, and step-by-step guidance — all at no cost. PropertyTaxHero is not affiliated with any similarly named websites.
          </p>

          <section className="mb-10">
            <div className="flex items-center mb-4"><Info className="text-tax-brightBlue mr-2 flex-shrink-0" /><h2 className="text-2xl font-semibold text-tax-deepBlue">Who We Are</h2></div>
            <div className="prose prose-slate max-w-none">
              <p className="text-tax-neutralGray mb-4"><a href="https://www.propertytaxhero.com/" className="text-tax-brightBlue hover:underline">PropertyTaxHero.com</a> is a free public service that helps homeowners better understand and review their property tax assessments.</p>
              <p className="text-tax-neutralGray mb-4">Our goal is to make it easier to access public assessment data and help you determine if your property may be overvalued.</p>
              <p className="text-tax-neutralGray mb-4">We believe that transparent information empowers better decisions — and our platform helps homeowners, agents, and professionals save time and serve their communities.</p>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center mb-4"><Shield className="text-tax-brightBlue mr-2 flex-shrink-0" /><h2 className="text-2xl font-semibold text-tax-deepBlue">Our Methodology</h2></div>
            <div className="prose prose-slate max-w-none text-tax-neutralGray">
              <p className="mb-4">PropertyTaxHero uses publicly available assessment data and recent comparable sales to help homeowners evaluate whether their property may be overassessed.</p>
              <p className="mb-4">Our tool retrieves your property's assessment details from public records, identifies comparable home sales in your area, and generates a report you can use when filing your appeal with your county assessor's office.</p>
              <p className="mb-4">We do not make the appeal decision for you. We provide data and guidance so you can make an informed decision.</p>
              <p><Link to="/methodology" className="text-tax-brightBlue hover:underline">Learn more about our methodology →</Link></p>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center mb-4"><FileText className="text-tax-brightBlue mr-2 flex-shrink-0" /><h2 className="text-2xl font-semibold text-tax-deepBlue">100% Free, Always</h2></div>
            <div className="prose prose-slate max-w-none">
              <ul className="list-disc pl-5 text-tax-neutralGray mb-4">
                <li className="mb-2">No fees, subscriptions, or upsells.</li>
                <li className="mb-2">We do not sell or share your information with third parties.</li>
                <li className="mb-2">PropertyTaxHero is supported by sponsors featured on the page.</li>
              </ul>
              <p className="text-tax-neutralGray">Learn more on our <Link to="/pricing" className="text-tax-brightBlue hover:underline">Pricing page</Link>.</p>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center mb-4"><Info className="text-tax-brightBlue mr-2 flex-shrink-0" /><h2 className="text-2xl font-semibold text-tax-deepBlue">Important Disclaimers</h2></div>
            <div className="prose prose-slate max-w-none">
              <h3 className="text-lg font-medium text-tax-deepBlue mb-2">This tool is not a substitute for local expertise.</h3>
              <p className="text-tax-neutralGray mb-4">While our system can identify comparable properties, a licensed local real estate agent is best equipped to assess unique features, remodeling, and neighborhood nuances.</p>
              <h3 className="text-lg font-medium text-tax-deepBlue mb-2">We do not offer individual support.</h3>
              <p className="text-tax-neutralGray mb-4">This is a self-service platform. The data presented is pulled from public record sources and third-party providers. We cannot edit or correct any property details.</p>
              <h3 className="text-lg font-medium text-tax-deepBlue mb-2">No legal advice.</h3>
              <p className="text-tax-neutralGray mb-6">This website does not constitute legal, tax, or real estate advice. For personalized guidance, consult a licensed attorney, accountant, or real estate professional in your area.</p>
            </div>
          </section>

          <section className="mb-10">
            <div className="flex items-center mb-4"><FileText className="text-tax-brightBlue mr-2 flex-shrink-0" /><h2 className="text-2xl font-semibold text-tax-deepBlue">Contact Info</h2></div>
            <div className="prose prose-slate max-w-none">
              <p className="text-tax-neutralGray mb-2">PropertyTaxHero.com is operated by YourWayHome.com LLC from:</p>
              <p className="text-tax-neutralGray mb-4">2205 136th Ave, Suite 105 #107<br />Broomfield, CO 80023</p>
              <p className="text-tax-neutralGray mb-6">
                Email for inquiries: <a className="text-tax-brightBlue hover:underline" href="mailto:support@propertytaxhero.com">support@propertytaxhero.com</a><br />
                Please note: We do not offer direct customer support.
              </p>
              <p className="text-tax-neutralGray"><Link to="/contact" className="text-tax-brightBlue hover:underline">View our full contact page →</Link></p>
            </div>
          </section>

          {!isLoggedIn &&
          <div className="mt-10 mb-6 text-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/#property-form">Check your property assessment for free →</Link>
              </Button>
            </div>
          }
        </article>
      </div>
      <Footer />
    </div>);

};

export default AboutUs;